import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, Facebook, Instagram, Twitter, Linkedin, Phone, Mail, Globe, Send, UserRound, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { waLink, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useContent } from "@/lib/useContent";
import { useClientAuth } from "@/context/ClientAuthContext";
import { PaymentMethodIcons } from "@/components/PaymentMethods";
import { TrustBadges } from "@/components/PaymentMethods";
import { useBusinessSchema } from "@/lib/seo";

const nav = [
  { to: "/", label: "Home" },
  { to: "/destinations", label: "Destinations" },
  { to: "/holidays", label: "Holidays" },
  { to: "/flights", label: "Flights" },
  { to: "/visa", label: "Visas" },
  { to: "/hotels", label: "Hotels" },
  { to: "/tours", label: "Tours" },
  { to: "/about", label: "About Us" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/newsletter", { email });
      trackEvent("newsletter_subscribed", {});
      toast.success("You're subscribed! Watch your inbox for travel deals.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-5 flex gap-2" data-testid="newsletter-form">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address"
        className="flex-1 min-w-0 rounded-full bg-white/10 border border-white/20 px-5 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#D4A62A]"
        data-testid="newsletter-email-input" />
      <button type="submit" disabled={busy} className="shrink-0 w-11 h-11 rounded-full bg-[#D4A62A] hover:bg-[#E3BA45] text-white flex items-center justify-center transition-colors disabled:opacity-60" aria-label="Subscribe" data-testid="newsletter-submit-btn">
        <Send size={16} />
      </button>
    </form>
  );
};

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const PHONES = useContent("phones");
  const OFFICES = useContent("offices");
  const clientAuth = useClientAuth();
  const client = clientAuth?.client;
  const loc = useLocation();
  const isHome = loc.pathname === "/";
  const publicSchemaPaths = ["/", "/about", "/services", "/destinations", "/hotels", "/blog", "/legal", "/corporate", "/insurance", "/transfers", "/visa", "/flights", "/holidays", "/tours", "/contact"];
  useBusinessSchema(publicSchemaPaths.some((p) => loc.pathname === p || (p !== "/" && loc.pathname.startsWith(`${p}/`))));
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const transparent = isHome && !scrolled && !open;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Navbar */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${transparent ? "bg-transparent" : "bg-white/90 backdrop-blur-xl border-b border-gray-100 shadow-[0_2px_20px_rgba(10,46,77,0.05)]"}`} data-testid="site-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center shrink-0" data-testid="brand-home-link">
            <img src={transparent ? "/logo-white.png" : "/logo-color.png"} alt="Makchester Tours & Travel" className="h-11 w-auto object-contain" />
          </Link>

          <nav className="hidden xl:flex items-center gap-5">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"}
                className={({ isActive }) => `text-[13px] font-medium tracking-wide transition-colors pb-0.5 border-b-2 ${isActive ? "border-[#D4A62A]" : "border-transparent"} ${transparent ? "text-white/90 hover:text-white" : "text-[#4B5563] hover:text-[#0A2E4D]"} ${isActive && !transparent ? "text-[#0A2E4D] font-semibold" : ""}`}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-2.5 shrink-0">
            <Link to={client ? "/account" : "/account/login"}
               className={`inline-flex items-center gap-1.5 h-10 rounded-full px-3.5 text-xs font-semibold transition-colors ${transparent ? "bg-white/10 text-white hover:bg-white/20 border border-white/25" : "bg-[#F3F4F6] text-[#0A2E4D] hover:bg-[#E5E7EB]"}`}
               data-testid="nav-account-link">
              <UserRound size={15} /> {client ? client.name?.split(" ")[0] : "My Account"}
            </Link>
            <a href={waLink("Hello Makchester Tours & Travel, I would like to enquire.")} target="_blank" rel="noreferrer"
               className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${transparent ? "bg-white/10 text-white hover:bg-white/20 border border-white/25" : "bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366] hover:text-white"}`}
               aria-label="WhatsApp" data-testid="nav-whatsapp-cta">
              <MessageCircle size={18} />
            </a>
            <Link to="/holidays/request" className="mk-btn-gold !px-6 !py-2.5" data-testid="nav-book-now-btn">Book Now</Link>
          </div>

          <button className={`xl:hidden ${transparent ? "text-white" : "text-[#0A2E4D]"}`} onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="xl:hidden border-t border-gray-100 bg-white max-h-[calc(100vh-70px)] overflow-y-auto">
            <div className="px-6 py-4 flex flex-col gap-1">
              {nav.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"}
                  className={({ isActive }) => `text-sm py-2.5 ${isActive ? "text-[#0A2E4D] font-semibold" : "text-[#4B5563]"}`}>
                  {n.label}
                </NavLink>
              ))}
              <Link to={client ? "/account" : "/account/login"} className="text-sm py-2.5 text-[#4B5563] flex items-center gap-2" data-testid="mobile-account-link">
                <UserRound size={15} /> {client ? "My Account" : "Sign In / Register"}
              </Link>
              <div className="flex gap-2 mt-3 pb-2">
                <Link to="/holidays/request" className="mk-btn-gold flex-1 !py-3">Book Now</Link>
                <a href={waLink("Hello Makchester Tours & Travel, I would like to enquire.")} target="_blank" rel="noreferrer"
                   className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-3 text-sm font-semibold">
                  <MessageCircle size={16} /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className={`flex-1 ${isHome ? "" : "pt-[70px]"}`}><Outlet /></main>

      {/* Footer */}
      <footer className="bg-[#051A2E] text-slate-300" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <img src="/logo-white.png" alt="Makchester Tours & Travel" className="h-14 w-auto object-contain mb-2" />
            <p className="font-serif-display italic text-[#E3BA45] text-sm mb-5" data-testid="footer-tagline">Your Gateway to the World</p>
            <p className="text-sm text-slate-400 leading-relaxed max-w-sm">Makchester Tours &amp; Travel is an international travel company registered in the United Arab Emirates, Zimbabwe and South Africa, serving travellers worldwide.</p>
            <div className="flex gap-3 mt-6">
              {[{ Icon: Facebook, l: "Facebook" }, { Icon: Instagram, l: "Instagram" }, { Icon: Twitter, l: "Twitter" }, { Icon: Linkedin, l: "LinkedIn" }].map(({ Icon, l }) => (
                <a key={l} href="#" aria-label={l} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#D4A62A] hover:border-[#D4A62A] transition-colors"><Icon size={15} /></a>
              ))}
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4A62A] font-bold mt-8">Newsletter</p>
            <p className="text-xs text-slate-400 mt-2">Exclusive deals and visa updates, straight to your inbox.</p>
            <Newsletter />
          </div>

          <div>
            <h4 className="text-[#D4A62A] text-xs uppercase tracking-[0.2em] mb-5 font-bold">Quick Links</h4>
            <ul className="space-y-2.5 text-sm">
              {nav.slice(0, 7).map((n) => <li key={n.to}><Link to={n.to} className="hover:text-[#D4A62A] transition-colors">{n.label}</Link></li>)}
            </ul>
          </div>

          <div>
            <h4 className="text-[#D4A62A] text-xs uppercase tracking-[0.2em] mb-5 font-bold">Support</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/blog" className="hover:text-[#D4A62A] transition-colors">Travel Blog</Link></li>
              <li><Link to="/contact" className="hover:text-[#D4A62A] transition-colors">Contact Us</Link></li>
              <li><Link to="/#faq" className="hover:text-[#D4A62A] transition-colors">FAQ</Link></li>
              <li><Link to="/legal/privacy-policy" className="hover:text-[#D4A62A] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link></li>
              <li><Link to="/legal/terms-and-conditions" className="hover:text-[#D4A62A] transition-colors" data-testid="footer-terms-link">Terms &amp; Conditions</Link></li>
              <li><Link to="/legal/refund-policy" className="hover:text-[#D4A62A] transition-colors" data-testid="footer-refund-link">Refund Policy</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-[#D4A62A] text-xs uppercase tracking-[0.2em] mb-5 font-bold">Global Contact Centre</h4>
            <ul className="space-y-3 text-sm">
              {PHONES.map((p) => (
                <li key={p.tel}><a href={`tel:${p.tel}`} className="flex items-center gap-2 hover:text-[#D4A62A] transition-colors"><Phone size={13} className="text-[#D4A62A]" /> {p.number} <span className="text-slate-500 text-xs">{p.flag || ""}</span></a></li>
              ))}
              <li><a href="mailto:explore@makchester.com" className="flex items-center gap-2 hover:text-[#D4A62A] transition-colors"><Mail size={13} className="text-[#D4A62A]" /> explore@makchester.com</a></li>
              <li className="flex items-center gap-2"><Globe size={13} className="text-[#D4A62A]" /> makchester.com</li>
            </ul>
            <h4 className="text-[#D4A62A] text-xs uppercase tracking-[0.2em] mb-4 mt-8 font-bold">Global Offices</h4>
            <ul className="space-y-3 text-xs text-slate-400 leading-relaxed">
              {OFFICES.map((o) => (
                <li key={o.country}><span className="text-slate-200 font-semibold">{o.flag ? `${o.flag} ` : ""}{o.country}</span><br />{o.address}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col gap-4" data-testid="footer-payment-methods">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#D4A62A] flex items-center justify-center md:justify-start gap-1.5"><Lock size={12} /> Secure Online Payments</p>
                <p className="text-xs text-slate-400 mt-1.5">Pay safely using internationally accepted payment methods.</p>
              </div>
              <PaymentMethodIcons dark className="justify-center" />
            </div>
            <TrustBadges dark className="justify-center md:justify-start" />
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 gap-2">
            <span>© {new Date().getFullYear()} Makchester Tours &amp; Travel. All rights reserved. Registered in UAE, Zimbabwe &amp; South Africa.</span>
            <Link to="/admin/login" className="hover:text-[#D4A62A]" data-testid="footer-admin-link">Staff Login</Link>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a href={waLink("Hello Makchester Tours & Travel, I would like assistance.")} target="_blank" rel="noreferrer"
         className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-50 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white p-3 sm:px-4 sm:py-3.5 rounded-full shadow-lg shadow-black/20 transition-transform hover:scale-105 mk-whatsapp-pulse"
         data-testid="floating-whatsapp-cta">
        <MessageCircle size={18} /> <span className="hidden sm:inline text-sm font-semibold">Chat with us</span>
      </a>
    </div>
  );
}
